import { StandardRequest, StandardResponse } from "./functions";
import { MediaModel } from "./media";
import { ILocation } from "./location";
import { ITagAssociationMeta, TagModel } from "./tag";
import {
  IEntityContactMeta,
  IEntityGroupMeta,
  IEntityMediaMeta,
  IEntityMemoryMeta,
  IEntityPlaceMeta,
  IEntityAnchorMeta,
} from "./entity-links";
import type { ContactModel } from "./contacts";
import type { GroupModel } from "./groups";
import type { MemoryModel } from "./memories";
import type { PlaceModel } from "./places";
import { IUnifiedFilter } from "./filters";
import { ShareSummary, SharedToSummary, ISharedEntity } from "./shares";
import { IDate } from "./date";

/**
 * Categorical type of an Anchor. Strictly uses lowercase strings for database persistence.
 */
export type AnchorType = "animals" | "hobbies" | "courses" | "other";

/**
 * Raw persisted database document schema for an Anchor entity in Firestore (`anchors/{id}`).
 */
export interface IAnchor {
  /** Unique Firestore document identifier */
  id?: string;
  /** Primary identifier of the user who owns this anchor (FK -> appusers.userId) */
  ownerId: string;
  /** Canonical headline display name of the anchor */
  name: string;
  /** Categorical anchor type */
  type: AnchorType;
  /** Rich-text or narrative description of the anchor */
  description?: string;
  /** Chronological date or structured date payload */
  date?: string | IDate;
  /** Human-readable street or postal address */
  address?: string;
  /** Normalized geocoded location JSON block (latitude, longitude, text) */
  location?: ILocation;
  /** Direct latitude coordinate */
  latitude?: number;
  /** Direct longitude coordinate */
  longitude?: number;
  /** Primary cover photo media ID foreign key referencing media.id */
  photoMediaId?: string;
  /** Favourite rating from 1 to 5 (default 3) */
  favouriteRating?: number;
  /** Visibility toggle flag */
  isActive?: boolean;

  /** Foreign key relationship map of associated memories Record<memoryId, IEntityMemoryMeta> */
  memories?: Record<string, IEntityMemoryMeta>;
  /** Companion synchronized array of memory IDs maintained for Firestore query indexing */
  memoryIds?: string[];

  /** Foreign key relationship map of associated media Record<mediaId, IEntityMediaMeta> */
  media?: Record<string, IEntityMediaMeta>;
  /** Companion synchronized array of media IDs maintained for Firestore query indexing */
  mediaIds?: string[];

  /** Foreign key relationship map of associated contacts Record<contactId, IEntityContactMeta> */
  contacts?: Record<string, IEntityContactMeta>;
  /** Companion synchronized array of contact IDs maintained for Firestore query indexing */
  contactIds?: string[];

  /** Foreign key relationship map of associated groups Record<groupId, IEntityGroupMeta> */
  groups?: Record<string, IEntityGroupMeta>;
  /** Companion synchronized array of group IDs maintained for Firestore query indexing */
  groupIds?: string[];

  /** Foreign key relationship map of associated places Record<placeId, IEntityPlaceMeta> */
  places?: Record<string, IEntityPlaceMeta>;
  /** Companion synchronized array of place IDs maintained for Firestore query indexing */
  placeIds?: string[];

  /** Foreign key relationship map of associated child/related anchors Record<anchorId, IEntityAnchorMeta> */
  anchors?: Record<string, IEntityAnchorMeta>;
  /** Companion synchronized array of anchor IDs maintained for Firestore query indexing */
  anchorIds?: string[];

  /** Foreign key relationship map of associated tags Record<tagId, ITagAssociationMeta> */
  tags?: Record<string, ITagAssociationMeta>;
  /** Companion synchronized array of tag IDs maintained for Firestore query indexing */
  tagIds?: string[];

  /** Epoch timestamp in milliseconds when the anchor was created */
  createdAt: number;
  /** Epoch timestamp in milliseconds of the last modification */
  updatedAt?: number;
}

export type Anchor = IAnchor;

/**
 * Hydrated presentation view model for an Anchor entity returned over APIs and consumed by frontend clients.
 */
export type AnchorModel = Anchor & ISharedEntity & {
  /** Canonical hydrated Media view models associated with this anchor */
  mediaItems?: MediaModel[];
  /** Canonical hydrated Memory view models associated with this anchor */
  memoryItems?: MemoryModel[];
  /** Canonical hydrated Contact view models associated with this anchor */
  contactItems?: ContactModel[];
  /** Canonical hydrated Group view models associated with this anchor */
  groupItems?: GroupModel[];
  /** Canonical hydrated Place view models associated with this anchor */
  placeItems?: PlaceModel[];
  /** Canonical hydrated Anchor view models associated with this anchor */
  anchorItems?: AnchorModel[];
  /** Canonical hydrated Tag view models associated with this anchor */
  tagItems?: TagModel[];

  /** Signed tokenized thumbnail URL for the anchor cover photo */
  photoUrl?: string;

  /** Caller authorization role on this anchor */
  userRole?: "owner" | "editor" | "viewer";
  /** Whether caller has permission to share this anchor */
  canShare?: boolean;
  /** Human-readable display label for the anchor's owner */
  ownerDisplayName?: string;

  /** Summaries of active shares targeting this anchor */
  shareSummaries?: ShareSummary[];
  /** Recipient targets this anchor is shared with */
  sharedToSummaries?: SharedToSummary[];
};

/**
 * Request payload for creating a new Anchor entity.
 */
export type CreateAnchorRequest = StandardRequest & {
  name: string;
  type: AnchorType;
  description?: string;
  date?: string | IDate;
  address?: string;
  location?: ILocation;
  latitude?: number;
  longitude?: number;
  photoMediaId?: string;
  favouriteRating?: number;
  tags?: Record<string, ITagAssociationMeta>;
  memories?: Record<string, IEntityMemoryMeta>;
  media?: Record<string, IEntityMediaMeta>;
  contacts?: Record<string, IEntityContactMeta>;
  groups?: Record<string, IEntityGroupMeta>;
  places?: Record<string, IEntityPlaceMeta>;
  anchors?: Record<string, IEntityAnchorMeta>;
};

/**
 * Response payload returned after creating an Anchor.
 */
export type CreateAnchorResponse = StandardResponse & {
  anchor?: AnchorModel;
  id?: string;
};

/**
 * Request payload for retrieving a single Anchor entity by ID.
 */
export type GetAnchorRequest = StandardRequest & {
  anchorId?: string;
  id?: string;
};

/**
 * Response payload returning a single hydrated Anchor entity.
 */
export type GetAnchorResponse = StandardResponse & {
  anchor?: AnchorModel;
};

/**
 * Request payload for querying anchors owned by or shared with the caller.
 */
export type GetMyAnchorsRequest = StandardRequest & {
  filter?: IUnifiedFilter;
};

/**
 * Response payload returning a list of hydrated Anchor view models.
 */
export type GetMyAnchorsResponse = StandardResponse & {
  anchors: AnchorModel[];
};

/**
 * Request payload for modifying an existing Anchor entity.
 */
export type EditAnchorRequest = StandardRequest & {
  anchorId?: string;
  id?: string;
  name?: string;
  type?: AnchorType;
  description?: string;
  date?: string | IDate;
  address?: string;
  location?: ILocation;
  latitude?: number;
  longitude?: number;
  photoMediaId?: string;
  favouriteRating?: number;
  isActive?: boolean;
  tags?: Record<string, ITagAssociationMeta>;
  tagIds?: string[];
  memories?: Record<string, IEntityMemoryMeta>;
  memoryIds?: string[];
  media?: Record<string, IEntityMediaMeta>;
  mediaIds?: string[];
  contacts?: Record<string, IEntityContactMeta>;
  contactIds?: string[];
  groups?: Record<string, IEntityGroupMeta>;
  groupIds?: string[];
  places?: Record<string, IEntityPlaceMeta>;
  placeIds?: string[];
  anchors?: Record<string, IEntityAnchorMeta>;
  anchorIds?: string[];
};

/**
 * Response payload after editing an anchor.
 */
export type EditAnchorResponse = StandardResponse & {
  anchor?: AnchorModel;
};

/**
 * Request payload for deleting an Anchor entity.
 */
export type DeleteAnchorRequest = StandardRequest & {
  anchorId?: string;
  id?: string;
  cascadeUnlink?: boolean;
};

/**
 * Response payload after deleting an anchor.
 */
export type DeleteAnchorResponse = StandardResponse & {
  deleted?: boolean;
};

/**
 * Request payload for filtered anchor search.
 */
export type GetFilteredAnchorsRequest = StandardRequest & {
  filter?: IUnifiedFilter;
};

/**
 * Response payload returning filtered anchors.
 */
export type GetFilteredAnchorsResponse = StandardResponse & {
  anchors: AnchorModel[];
  totalCount?: number;
};

/**
 * Request payload to retrieve all platform anchors (administrator only).
 */
export type GetAllAnchorsRequest = StandardRequest & {
  filter?: IUnifiedFilter;
};

/**
 * Response payload returning all platform anchors.
 */
export type GetAllAnchorsResponse = StandardResponse & {
  anchors?: AnchorModel[];
};
